import { Grid } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { isManualTareLineItem } from 'src/common/helpers/action-window-helper';
import { convertCurrency } from 'src/common/helpers/currency-helper';
import { itemMeasureAttributes } from 'src/common/helpers/item-quantity-measure';
import { TbrData } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { AppState } from 'src/common/redux/configure-store';
import { CartItemViewData } from 'src/items-list/business-logic/service/interfaces/cart-response';
import {
  getTbrNetAvailableItemsTotal,
  getTbrReturnItemsTotal,
} from '../helper/tbr-item-total-price-helper';
import { styles } from './action-window-styles';
import ActionWindowLinkedLines from './linked-lines';

const TBRItemDetailLine: React.FC<TbrItemDetailsProps> = ({
  tbrData,
  item,
  isTBRActive,
  isWeighted,
}: TbrItemDetailsProps): JSX.Element => {
  const classes = styles({});
  const { t } = useTranslation();

  const {
    baseUnit,
    unitOfMeasure,
    baseFinalLinePrice,
    atomicMsuUnit,
    atomicMsuPrice,
    atomicMsuUnitForPrice,
    isMsu,
  } = itemMeasureAttributes(item, tbrData, isTBRActive);
  // TODO: Need to revist this once we are able to return a TW item in tbr and return mode
  const isLinkedItemTare = item.linkedLines?.filter(
    i => i.parentLineLinkType === 'Tare' || i.itemType === 'Tare',
  ).length;
  const isManualTareItem = isManualTareLineItem(item);
  return (
    <>
      <Grid container>
        <Grid item className={classes.alignCenter} data-testid='action-window-amount-line'>
          <span className={classes.lineItem} data-testid='action-window-base-unit-text'>
            ({baseUnit} {unitOfMeasure}
          </span>
          <span className={classes.lineItem} data-testid='action-window-quantifier-text'>
            {isLinkedItemTare || isManualTareItem ? t('TareNet') : ' '}
          </span>
          <span className={classes.lineItem} data-testid='action-window-item-price-per-unit'>
            x {atomicMsuPrice}
          </span>
          <span className={classes.UOMText} data-testid='action-window-unit-text'>
            {` / `}
            {isMsu ? `${atomicMsuUnitForPrice} ` : atomicMsuUnitForPrice}
            {atomicMsuUnit}
          </span>
          <span className={classes.lineItem}>)</span>
        </Grid>
        <Grid item className={clsx(classes.alignCenter, classes.autoLeftMargin)}>
          <span className={classes.lineItem}>
            {convertCurrency(Number(tbrData.ExtendedAmount || baseFinalLinePrice))}
          </span>
        </Grid>
      </Grid>
      {item?.linkedLines?.map((lineitem: CartItemViewData) => (
        <ActionWindowLinkedLines
          key={lineitem.description}
          linkedLine={lineitem}
          parentLine={item}
          atomicMsuUnit={atomicMsuUnit}
          tbrData={tbrData}
        />
      ))}
      {/* partial return component */}
      {tbrData.tbrReturnQty !== tbrData.tbrAvailableQty && (
        <PartialReturnLines
          tbrData={tbrData}
          item={item}
          unitOfMeasure={unitOfMeasure}
          isWeighted={isWeighted}
        />
      )}
    </>
  );
};

interface PartialReturnLinesProps {
  tbrData: TbrData;
  item: CartItemViewData;
  unitOfMeasure: string;
  isWeighted: boolean;
}

const PartialReturnLines: React.FC<PartialReturnLinesProps> = ({
  tbrData,
  item,
  unitOfMeasure,
  isWeighted,
}: PartialReturnLinesProps): JSX.Element => {
  const classes = styles({});
  const { t } = useTranslation();
  const defaultPrecision = useSelector(
    (state: AppState) =>
      state.touchPoint.touchPointConfiguration.defaultWeightUnitOfMeasurePrecision,
  );
  const remainingQuantity = tbrData.tbrReturnQty;
  const availableQuantity = (tbrData.tbrAvailableQty || 0) - (tbrData.tbrReturnQty || 0);
  return (
    <>
      <Grid container className={classes.marginTop1Vh}>
        <Grid item className={classes.alignCenter}>
          <span className={classes.lineItem} data-testid='action-window-tbr-return-quantity-count'>
            ({isWeighted ? remainingQuantity?.toFixed(defaultPrecision) : remainingQuantity}{' '}
            {item.lineItem?.quantity?.unitOfMeasure !== 'EA' && `${unitOfMeasure} `}
          </span>
          <span className={classes.lineItem} data-testid='action-window-tbr-return-item-text'>
            {t('ItemDetailsView_Return')})
          </span>
        </Grid>
        <Grid item className={clsx(classes.alignCenter, classes.footer, classes.autoLeftMargin)}>
          <span className={classes.lineItem} data-testid='action-window-tbr-return-total'>
            -{convertCurrency(getTbrReturnItemsTotal(item, tbrData))}
          </span>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item className={clsx(classes.alignCenter, classes.marginTop1Vh)}>
          <span
            className={classes.lineItem}
            data-testid='action-window-tbr-net-available-quantity-count'
          >
            ({isWeighted ? availableQuantity.toFixed(defaultPrecision) : availableQuantity}{' '}
            {unitOfMeasure !== 'EA' && `${unitOfMeasure} `}
          </span>
          <span
            className={classes.lineItem}
            data-testid='action-window-tbr-net-available-item-text'
          >
            {t('Available')})
          </span>
        </Grid>
        <Grid item className={clsx(classes.alignCenter, classes.footer, classes.autoLeftMargin)}>
          <span className={classes.lineItem} data-testid='action-window-tbr-available-total'>
            {convertCurrency(getTbrNetAvailableItemsTotal(item, tbrData))}
          </span>
        </Grid>
      </Grid>
    </>
  );
};

export interface TbrItemDetailsProps {
  tbrData: TbrData;
  item: CartItemViewData;
  isTBRActive: boolean;
  isWeighted: boolean;
}

export default TBRItemDetailLine;
